import * as Yup from 'yup'
import FormBuilder, { FormFields } from "app/modules/tp/builders/FormBuilder";
import moment from 'moment';
import { Driver } from "../_models";
import { Provider } from '../../providers/_models';
import ProviderAPI from '../../providers/_requests';
import { dateFormat, DateRangeState, timeFormat, TimeRangeState } from 'app/partials/form/DatetimeRangeInput';
import { RideTable } from '../partials/RideTable';
import { FormikProps } from 'formik';

const isDateStateValid = (dates: DateRangeState): boolean => (
    !!(dates?.from && dates?.to && moment(dates.from, dateFormat).isBefore(moment(dates.to, dateFormat)))
)

const isTimeStateValid = (times: TimeRangeState): boolean => (
    !!(times.date && times.from && times.to && moment(`${times.date} ${times.from}`, `${dateFormat} ${timeFormat}`).isBefore(moment(`${times.date} ${times.to}`, `${dateFormat} ${timeFormat}`)))
)

const generateEntityForm = async () => {
    const response = await ProviderAPI.getAllNoPagination({});
    let providers: Array<Provider> = [];

    if (response?.success) {
        providers = response.data as Array<Provider>;
    }

    const formFields: FormFields<Driver> = {
        title0: {
            type: "titleView",
            title: "Info Personali",
            icon: <i className="fa-solid fa-address-card"></i>,
            columnNo: 0
        },
        id: {
            type: "text",
            key: "id",
            label: "# ID",
            attributes: {
                disabled: true
            },
            validation: Yup.number().integer().nullable(),
            columnNo: 0,
        },
        provider: {
            type: "select",
            key: "provider",
            label: "Fornitore",
            isMulti: false,
            options: providers.map(provider => ({ value: (provider.id?.toString()), label: `${provider.name ?? ""} ${provider.surname ?? ""}` })),
            columnNo: 0,
        },
        name: {
            type: "text",
            key: "name",
            label: "Nome",
            validation: Yup.string()
                .required('Il nome è obbligatorio')
                .max(50, 'Il nome visualizzato deve essere di massimo 50 caratteri'),
            columnNo: 0,
        },
        surname: {
            type: "text",
            key: "surname",
            label: "Cognome",
            validation: Yup.string().nullable()
                .required('Il cognome è obbligatorio')
                .max(50, 'Il cognome deve essere di massimo 50 caratteri'),
            columnNo: 0,
        },
        phone: {
            type: "text",
            key: "phone",
            label: "Telefono",
            validation: Yup.string().nullable()
                .min(10, 'Il telefono deve avere almeno 10 caratteri')
                .max(14, 'Il telefono deve avere al massimo 11 caratteri')
                .matches(/^(\+)?[0-9]+$/, 'Il telefono deve essere valido'),
            columnNo: 0,
        },
        email: {
            type: "text",
            key: "email",
            label: "Email",
            validation: Yup.string().nullable()
                .max(50, 'L\'email deve essere di massimo 50 caratteri')
                .nullable()
                .email('L\'email deve essere valida'),
            columnNo: 0,
        },
        identity_card: {
            type: "text",
            key: "identity_card",
            label: "N. Carta d'Identità",
            validation: Yup.string().nullable()
                .max(9, 'Il numero della carta d\'identità deve essere di esattamente 9 caratteri')
                .matches(/^[a-zA-Z0-9]+$/, 'Il numero della carta d\'identità deve essere valido'),
            columnNo: 0,
        },
        document: {
            type: "file_base64",
            fileTypes: ['application/pdf'],
            key: "document",
            label: "Documento",
            validation: Yup.mixed().nullable(),
            columnNo: 0,
        },
        title1: {
            type: "titleView",
            title: "Giorni di Assenza",
            icon: <i className="fa-solid fa-calendar"></i>,
            columnNo: 1
        },
        driverDaysAbsences: {
            type: "absencesdaterange",
            selectOpt: [
                { label: "Malattia", value: "malattia" },
                { label: "Ferie", value: "ferie" },
                { label: "Permessi", value: "permessi" },
                { label: "Riposo regolare", value: "riposo_regolare" },
                { label: "Riposo ridotto", value: "riposo_ridotto" },
                { label: "Altro", value: "altro" },
            ],
            dateRange: true,
            multiple: true,
            addLabel: <>Aggiungi giorni</>,
            key: "driverDaysAbsences",
            label: "Giorni di assenza",
            validation: Yup.array().of(Yup.object().shape({
                from: Yup.string().required('La data di inizio è obbligatoria'),
                to: Yup.string().required('La data di arrivo è obbligatoria'),
            }))
                // .test('check-validity', "La data di arrivo deve essere maggiore della data di inizio", (value) => (
                //     !!value?.every((item) =>  isDateStateValid(item as DateRangeState))
                // ))
                .nullable(),
            columnNo: 1,
        },
        title2: {
            type: "titleView",
            title: "Ore di Assenza",
            icon: <i className="fa-solid fa-calendar"></i>,
            columnNo: 2
        },
        driverHoursAbsences: {
            type: "absencestimerange",
            multiple: true,
            selectOpt: [
                { label: "Malattia", value: "malattia" },
                { label: "Ferie", value: "ferie" },
                { label: "Permessi", value: "permessi" },
                { label: "Riposo regolare", value: "riposo_regolare" },
                { label: "Riposo ridotto", value: "riposo_ridotto" },
                { label: "Altro", value: "altro" },
            ],
            hoursRange: true,
            key: "driverHoursAbsences",
            label: "Ore di assenza",
            addLabel: <>Aggiungi ore</>,
            validation: Yup.array().of(Yup.object().shape({
                from: Yup.string().required("L'ora di inizio è obbligatoria"),
                to: Yup.string().required("L'ora di arrivo è obbligatoria"),
            }))
                // .test('check-validity', "L'ora di arrivo deve essere maggiore dell'ora di inizio", (value) => (
                //     !!value?.every((item) =>  isTimeStateValid(item as TimeRangeState))
                // ))
                .nullable(),
            columnNo: 2,
        },
    }
    return formFields;
}

const generateRelationTable = async () => {
    const formFields: FormFields<Driver> = {
        custom: {
            type: "custom",
            key: "custom",
            render: (f: FormikProps<Driver>, driver?: Driver) => { return <RideTable entities={f?.values["rides"]} filters={{ driver: (driver?.id ? driver.id : -1) }} /> },
        },
    }
    return formFields;
}

const DriverFormBuilder = async () => {
    const relTable: FormFields<Driver> = await generateRelationTable();
    const entityForm: FormFields<Driver> = await generateEntityForm();
    return FormBuilder<Driver>({
        formFields:
        {
            hasTabs: true,
            tabs: [
                {
                    name: "Anagrafica",
                    fields: entityForm
                },
                {
                    name: "Corse Assegnate",
                    fields: relTable
                },
            ]
        }
    });
};

export default DriverFormBuilder;
